import { StyledRange } from "@silevis/reactgrid";
import { getIn } from "final-form";
import moment from "moment";
import {
  ApplicationContextType,
  FinancialStatementClassificationStatus,
  UnderwritingSteps,
  uploadStates,
} from "../../../interfaces/interfaces";

const fyClassificationProcessingStatuses = new Set<string>([
  FinancialStatementClassificationStatus.PENDING,
  FinancialStatementClassificationStatus.PROCESSING,
]);

/** True while the FY classification job is queued or running (not after upload alone). */
export const isFyClassificationJobProcessing = (
  fyClassificationData: { status?: string } | null | undefined
): boolean => {
  if (!fyClassificationData?.status) return false;
  const s = String(fyClassificationData.status).toUpperCase();
  return fyClassificationProcessingStatuses.has(s);
};
/**
 * Latest month we can expect books to be closed for.
 *
 * Mirrors `getMisRequiredPeriod` on the API (src/common/functions.ts): before the
 * 15th the latest closed month is two months back, from the 15th onwards it is one.
 * Both the FY provisional cut-off and the MIS window hang off this, so the copy
 * never asks for a period the backend does not expect.
 */
const getClosedBooksAnchorMonth = () => {
  const today = moment();
  const monthsBack = today.date() >= 15 ? 1 : 2;
  // startOf('month') before subtracting: Mar 31 minus one month is Feb 28
  return today.clone().startOf("month").subtract(monthsBack, "months");
};

export const MIS_MONTHS_COUNT = 12;

/**
 * The `missingDocs` entry a missing MIS reports. Phrased as the action to take,
 * like the other upload errors ("Upload Financials"), and shared with the
 * checklist so it can tell a missing MIS apart from missing financials.
 */
export const MIS_UPLOAD_ERROR = "Upload MIS";

/**
 * "Jul 31" — end of the last month books are expected to be closed for, which is
 * as far as a provisional Balance Sheet for the running FY can go.
 *
 * Null early in a financial year, when that month still falls in the previous FY
 * and the card therefore has no cut-off of its own to name.
 */
export const getFyProvisionalUntilLabel = (
  financialYearDetails: { fyStart?: string } | null | undefined
): string | null => {
  const fyStart = moment(financialYearDetails?.fyStart, "DD-MM-YYYY");
  const anchorMonthEnd = getClosedBooksAnchorMonth().endOf("month");
  if (!fyStart.isValid() || anchorMonthEnd.isBefore(fyStart, "day")) {
    return null;
  }
  return anchorMonthEnd.format("MMM DD");
};

/**
 * The "nothing uploaded yet" error for a financial year card.
 *
 * Both halves follow what that year can legitimately produce: the running FY has
 * only provisional figures, and only up to the last closed month; a just-closed FY
 * may be either provisional or audited; once its audit deadline passes it must be
 * audited. `fyLabel` is the short form, e.g. "2025-26".
 */
export const getFyMissingDocsError = (
  financialYearDetails:
    | { fyPassed?: boolean; isAuditedFinancialRequired?: boolean }
    | null
    | undefined,
  fyLabel: string
): { title: string; action: string } => {
  const isFyPassed = !!financialYearDetails?.fyPassed;
  const isAuditRequired = !!financialYearDetails?.isAuditedFinancialRequired;

  const type = !isFyPassed
    ? "provisional"
    : isAuditRequired
    ? "audited"
    : "provisional or audited";
  // "an audited", but "a provisional".
  const article = /^[aeiou]/i.test(type) ? "an" : "a";

  const period = isFyPassed
    ? `for FY ${fyLabel}`
    : `up to ${getClosedBooksAnchorMonth().endOf("month").format("MMM DD, YYYY")}`;

  return {
    title: "Financials not uploaded.",
    action: `Upload ${article} ${type} Balance Sheet and P&L ${period}.`,
  };
};

/**
 * The API's MIS period, `"2025-08 - 2026-07"`, as `"Aug 2025 - Jul 2026"`.
 * Null when absent or malformed, so the caller can fall back to the local label.
 */
export const formatMisPeriod = (
  period: string | null | undefined
): string | null => {
  const [start, end] = (period ?? "").split(" - ");
  const startMonth = moment(start, "YYYY-MM", true);
  const endMonth = moment(end, "YYYY-MM", true);
  if (!startMonth.isValid() || !endMonth.isValid()) {
    return null;
  }
  return `${startMonth.format("MMM YYYY")} - ${endMonth.format("MMM YYYY")}`;
};

/**
 * The rolling MIS window we ask for — the 12 months ending at the anchor month,
 * e.g. "Aug 2025 - Jul 2026". A fallback for before the API responds; the server's
 * own `requiredPeriod.period` is authoritative.
 */
export const getMisPeriodLabel = (): string => {
  const anchor = getClosedBooksAnchorMonth();
  const start = anchor.clone().subtract(MIS_MONTHS_COUNT - 1, "months");
  return `${start.format("MMM YYYY")} - ${anchor.format("MMM YYYY")}`;
};

import { findFirstDebtorScheduleGridError } from "./DebtorSchedule/debtorScheduleHelper";
import { hasNoUploadedDebt, isDebtIncomplete } from "../../../components/DebtSchedule/debtValidation";

export enum metricsName {
  grossSales = "Gross Sales",
  transactionDetails = "Transaction Details",
  productCosts = "Product Costs",
  logisticAndPackagingCosts = "Logistic and Packaging Costs",
  marketingExpenses = "Marketing Expenses",
  additionalCosts = "Additional Costs",
  operatingExpenses = "Operating Expenses",
  loanServicing = "Loan Servicing",
}

export const dividerMetrics = new Set([
  "Gross Sales",
  "Transaction Details",
  "Product Costs",
  "Logistic and Packaging Costs",
  "Marketing Expenses",
  "Additional Cost",
  "Operating Expenses",
  "Loan Servicing",
]);

export const generateProvisionalStatement = (
  startDate: string,
  endDate: string
) => {
  const endMonth = moment(endDate, "DD-MM-YYYY");
  const startMonth = moment(startDate, "DD-MM-YYYY");
  const differenceInMonths = endMonth.diff(startMonth, "months");
  const months = [startMonth.clone().format("MM-YYYY")];
  for (let i = 1; i <= differenceInMonths; i++) {
    months.push(startMonth.clone().add(i, "months").format("MM-YYYY"));
  }
  //   console.log("differenceInMonths", differenceInMonths);
  const headers = ["INR in Lakhs", ...months.reverse()];
  const metrics = [
    "Gross Sales",
    "D2C",
    "Amazon",
    "Myntra",
    "Offline",
    "Others",
    "Transaction Details",
    "Returns",
    "Refunds",
    "Collected Taxes",
    "Collected Shipping",
    "COD",
    "Others",
    "Product Costs",
    "Cost of Goods Sold",
    "Logistics and Packaging Costs",
    "Shipping Cost",
    "Packaging",
    "Marketing Expenses",
    "Meta",
    "Google",
    "Amazon",
    "Others",
    "Additional Cost",
    "Platform Commission",
    "Branding",
    "Agency Fees",
    "Operating Expenses",
    "Rent",
    "Salaries",
    "Loan Servicing",
    "Loan 1",
    "Loan 2",
    "Loan 3",
    "Loan 4",
  ];
  const rowData = metrics.map((metric) => {
    const monthEmptyArray = Array.from({ length: months.length }, () => "");
    return [metric, ...monthEmptyArray];
  });
  return {
    headers,
    rowData,
  };
};

export const getGridInvalidRange = (gridData: any) => {
  const newStyledRanges: StyledRange[] = [];

  gridData.rowData.forEach((row, rowIndex) => {
    if (dividerMetrics.has(row[0])) return; // Skip dividers

    row.forEach((cell, colIndex) => {
      if (cell.toString().trim() === "") {
        // If cell is empty
        newStyledRanges.push({
          range: {
            start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
            end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
          },
          styles: { background: "#FDF1F1" }, // Light red
        });
      }
    });
  });
  return newStyledRanges;
};

export const checkIfGridDataIsValid = (gridData: any) => {
  const isInvalid = gridData.rowData.some((row, rowIndex) => {
    if (dividerMetrics.has(row[0])) return; // Skip dividers
    return row.some((cell) => cell.toString().trim() === "");
  });
  return isInvalid;
};

export const revenueFormValidation = (values, underwritingFileJobs) => {
  const errors = {};
  const allRevenueJobs = getIn(
    underwritingFileJobs,
    `${UnderwritingSteps.revenue}`
  );
  const missingData = [];
  const missingDocs = [];
  const requirePassword = [];
  if (values?.revenueDetails?.length) {
    const revenueDetailsError = [];
    values.revenueDetails.forEach((revenueDetail, outerIndex) => {
      let revenueErrorObj = {};
      // The running FY (index 0) is optional: its books are still open, so we ask
      // for provisional figures but do not block the step on them. Every closed FY
      // is required. A password-protected upload is flagged on any FY, since a file
      // we cannot open is useless wherever it sits.
      const isDocsRequired = outerIndex !== 0;
      if (revenueDetail?.isAuditedFinancials) {
        const uploadedFiles = revenueDetail?.auditedFinancials ?? [];
        if (!uploadedFiles.length) {
          if (isDocsRequired) {
            revenueErrorObj["auditedFinancials"] = "Upload Financials";
            missingDocs.push("Upload Financials");
          }
        } else {
          const passwordRequired = uploadedFiles.some(
            (auditedFinancial, index) => {
              const uploadedFileJob =
                allRevenueJobs?.[
                  `revenueDetails[${outerIndex}].auditedFinancials[${index}]`
                ];
              if (uploadedFileJob?.status == uploadStates?.requiredPassword) {
                return true;
              }
            }
          );
          if (passwordRequired) {
            revenueErrorObj["auditedFinancials"] = "Password missing";
            requirePassword.push("Password missing");
          }
        }
      } else if (isDocsRequired) {
        if (!revenueDetail?.provisionalStatement) {
          revenueErrorObj["provisionalStatement"] = "Details Missing";
          missingData.push("Details Missing");
        } else {
          const invalidRange = checkIfGridDataIsValid(
            revenueDetail?.provisionalStatement
          );
          if (invalidRange) {
            revenueErrorObj["provisionalStatement"] = "Details Missing";
            missingData.push("Details Missing");
          }
        }
      }
      revenueDetailsError.push(revenueErrorObj);
    });
    if (revenueDetailsError.length) {
      errors["revenueDetails"] = revenueDetailsError;
    }
  }

  const misReports = values?.misReports ?? [];
  if (!misReports.length) {
    errors["misReports"] = MIS_UPLOAD_ERROR;
    missingDocs.push(MIS_UPLOAD_ERROR);
  } else {
    // A password-protected MIS is as unreadable as a password-protected FY
    // financial, so it blocks the step the same way.
    const passwordRequired = misReports.some((misReport, index) => {
      const uploadedFileJob = allRevenueJobs?.[`misReports[${index}]`];
      return uploadedFileJob?.status == uploadStates?.requiredPassword;
    });
    if (passwordRequired) {
      errors["misReports"] = "Password missing";
      requirePassword.push("Password missing");
    }
  }

  if (!values?.hasDebt) {
    errors["hasDebt"] = "Select an option";
    missingData.push("Select an option");
  }

  // Yes needs at least one uploaded sanction letter; a processed one needs its
  // outstanding balance, and a term loan its start date and repayment schedule.
  // Same rules the debt cards use to show their errors. Failed files and product
  // mismatches block only the checklist's final submit (see checklistHelper).
  if (
    values?.hasDebt == "yes" &&
    (hasNoUploadedDebt(values?.debt) || (values?.debt || []).some(isDebtIncomplete))
  ) {
    errors["debt"] = "Details Missing";
    missingData.push("Details Missing");
  }

  // if (!values?.hasReceivables) {
  //   errors["hasReceivables"] = "Select an option";
  //   missingData.push("Select an option");
  // }
  // if (values?.hasReceivables == "yes") {
  //   if (!values?.debtorSchedule) {
  //     errors["debtorSchedule"] = "Details Missing";
  //     missingData.push("Details Missing");
  //   } else {
  //     const invalidRange = findFirstDebtorScheduleGridError(
  //       values?.debtorSchedule,
  //       ApplicationContextType.underwriting
  //     );
  //     if (invalidRange) {
  //       errors["debtorSchedule"] = "Details Missing";
  //       missingData.push("Details Missing");
  //     }
  //   }
  // }

  // console.log("values", values);

  return {
    errors,
    missingData,
    missingDocs,
    requirePassword,
  };
};
